import React, { useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, CardHeader, CardBody, Spinner, Alert, Input, Button, Badge } from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { API } from "../../api/urls";
import useAnimatedPlaceholder, { SEARCH_FIELDS } from "../../hooks/useAnimatedPlaceholder";
import AccountDeleteModal from "../modals/accounts/AccountDeleteModal";
import { ACCOUNT_TYPES, ACCOUNT_TYPE_LABEL_MAP, ACCOUNT_TYPE_BADGE_COLORS } from "../../constants/accountTypes";
import TruncatedText from "../../components/Common/TruncatedText"
import StatCards, { STAT_ACCENTS } from "../../components/Common/StatCards";
import FeatherIcon from "feather-icons-react";
import { fetchFinalRates } from "../../api/requests/payment-requests";
import { summarizeBalancesInTry } from "../../utils/currencyUtils";

const TableCard = ({ className = "", children, ...rest }) => (
    <div className={`card rounded-4 border-0 shadow-lg ${className}`} style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }} {...rest}>
        {children}
    </div>
);

const NAVY = "#221C5D";
const ORANGE = "#E56A22";

const AccountsTable = () => {
    const [accounts, setAccounts] = useState([]);
    // Net bakiye TL karşılığı için firma bazlı kurlar. Kur firma ayarından gelir,
    // süper admin listesi birden çok firmayı kapsayabildiği için firma başına
    // önbelleğe alınır; aynı firma bir daha sorulmaz.
    const [ratesByCompany, setRatesByCompany] = useState({});
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [companyFilter, setCompanyFilter] = useState("");
    const [accountTypeFilter, setAccountTypeFilter] = useState("");

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [detailAccount, setDetailAccount] = useState({});
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [deleteAccount, setDeleteAccount] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const [cumulativeBalance, setCumulativeBalance] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(false);

    const isFirstSearch = useRef(true);
    const searchRef = useRef(null);
    const animatedPlaceholder = useAnimatedPlaceholder(SEARCH_FIELDS.accounts, { prefix: "Ara: " });
    const navigate = useNavigate();
    const translateAccountTypeFromConst = (t) => ACCOUNT_TYPE_LABEL_MAP[t] || t;
    const accountTypeColorFromConst = (t) => ACCOUNT_TYPE_BADGE_COLORS[t] || "#B0B7C3";

    document.title = "Cariler/Bayiler | Tahsilat360";

    const accountTypeColor = (t) => {
        switch (t) {
            case "SOLE_PROPRIETORSHIP":      return "#71C29B"; // Şahıs Şirketi
            case "JOINT_STOCK_COMPANY":      return "#7AA7F5"; // Anonim Şirket
            case "LIMITED_COMPANY":return "#221C5D"; // Limited Şirket (NAVY)
            case "COOPERATIVE":              return "#F2C96D"; // Kooperatif
            case "GENERAL_PARTNERSHIP":      return "#B0B7C3"; // Kolektif
            case "COMMANDITE_COMPANY":      return "#6C757D"; // Komandit
            default:                         return "#B0B7C3"; // Varsayılan
        }
    };

    const AccountTypeBadge = ({ type }) => (
        <span
            className="badge border-0"
            style={{
                borderRadius: 999,
                padding: ".35rem .5rem",
                backgroundColor: ACCOUNT_TYPE_BADGE_COLORS[type] || "#B0B7C3",
                color: "#fff"
            }}
            title={ACCOUNT_TYPE_LABEL_MAP[type] || type}
                >
          {ACCOUNT_TYPE_LABEL_MAP[type] || type}
        </span>
    );

    const styles = `
    .accounts-modern .table thead tr { background-color: #f9f9f9; }
    .accounts-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .accounts-modern .table td { vertical-align: middle; }
    .accounts-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .accounts-modern .search-input { width: 260px; }
    .accounts-modern .header-actions { gap: .5rem; }
    .accounts-modern .btn-rounded { border-radius: 999px; }
    .accounts-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .accounts-modern .btn-navy:hover { filter: brightness(1.03); }
    .accounts-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .accounts-modern .btn-orange:hover { filter: brightness(1.03); }
    .accounts-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .accounts-modern .pill-input, .accounts-modern .pill-select { border-radius: 999px !important; padding: .45rem .9rem; }

    .accounts-modern .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
    .accounts-modern .corporate-pagination .page-item.active .page-link { background-color: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .accounts-modern .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
    .accounts-modern .code-dark { color: #000 !important; }

    @media (max-width: 576px) {
      .accounts-modern .search-input { width: 100%; }
      .accounts-modern .table { font-size: 12px; }
      .accounts-modern .table td, .accounts-modern .table th { padding: .5rem; }
    }
    
    .accounts-modern .page-size-wrap { display:flex; align-items:center; gap:.5rem; white-space:nowrap; }
    .accounts-modern .page-size-select { min-width:70px; padding-right:1.75rem; }
  `;

    const fetchCompanies = async () => {
        try {
            const response = await axios.get(API.get_companies_names);
            setCompanies(response.data.data || []);
        } catch (err) {}
    };

    const fetchCumulativeBalance = async () => {
        try {
            setBalanceLoading(true);
            const response = await axios.get(API.get_cumulative_balance);
            setCumulativeBalance(response.data.data);
        } catch (err) {
            setCumulativeBalance(null);
        } finally {
            setBalanceLoading(false);
        }
    };

    const fetchAccounts = async (page = 0, size = 10, search = "", companyId = "", accountType = "", first = false) => {
        try {
            setError(null);
            if (first) setLoading(true); else setRefreshing(true);

            const params = { page, size };
            if (search) params.searchTerm = search;
            if (companyId) params.companyId = companyId;
            if (accountType) params.accountType = accountType;

            const { data } = await axios.get(API.accounts_list, { params, headers: {"Content-Type": "application/json" }} );
            const { content, totalElements, totalPages, page: cp, size: ps } = data.data;
            setAccounts(content);
            setTotalElements(totalElements);
            setTotalPages(totalPages);
            setCurrentPage(cp);
            setPageSize(ps);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/login");
            } else {
                setError(err.response?.data?.message || "Hesapları getirirken bir hata oluştu");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Listedeki firmaların kurlarını yükler. Sayfa başına en fazla birkaç firma
    // olduğu için istekler paralel gider; başarısız olan firma için TL karşılığı
    // hesaplanmaz ve hücre TRY bakiyesine düşer (uydurma kur kullanılmaz).
    useEffect(() => {
        const missing = [...new Set(accounts.map(a => a.companyId).filter(Boolean))]
            .filter(id => !(id in ratesByCompany));
        if (missing.length === 0) return;

        let cancelled = false;
        (async () => {
            const results = await Promise.all(missing.map(async (id) => {
                try {
                    const res = await fetchFinalRates(id);
                    if (!res.ok) return [id, null];
                    const { data } = await res.json();
                    return [id, data || null];
                } catch {
                    return [id, null];
                }
            }));
            if (!cancelled) {
                setRatesByCompany(prev => ({ ...prev, ...Object.fromEntries(results) }));
            }
        })();
        return () => { cancelled = true; };
    }, [accounts]); // eslint-disable-line

    /**
     * Carinin net bakiyesi — detay ekranındaki "Net Toplam Bakiye (TL Karşılığı)"
     * ile aynı hesap (summarizeBalancesInTry). Döviz bakiyeleri firma kuruyla
     * TL'ye çevrilip toplanır; kur yoksa yalnız TRY bakiyesi gösterilir ve
     * çevrilemeyen para birimleri ipucunda belirtilir.
     */
    const renderNetBalance = (a) => {
        const map = a.balances || {};
        const list = Object.entries(map).map(([currency, currentBalance]) => ({ currency, currentBalance }));
        if (list.length === 0) return formatCurrencyCode(a.balance ?? 0, "TRY");

        const { hasConverted, netBalance, unconvertedCurrencies } =
            summarizeBalancesInTry(list, ratesByCompany[a.companyId]);

        if (hasConverted && unconvertedCurrencies.length === 0) {
            return formatCurrencyCode(netBalance, "TRY");
        }
        const tryOnly = Number(map.TRY ?? a.balance ?? 0);
        const missing = unconvertedCurrencies.filter(c => (c || "").toUpperCase() !== "TRY");
        return (
            <span title={missing.length
                ? `${missing.join(", ")} bakiyesi kur tanımlı olmadığı için TL'ye çevrilemedi`
                : undefined}>
                {formatCurrencyCode(hasConverted ? netBalance : tryOnly, "TRY")}
                {missing.length > 0 && <span className="text-muted ms-1" style={{ fontSize: ".75rem" }}>*</span>}
            </span>
        );
    };

    const handleExportExcel = async () => {
        try {
            setExporting(true);
            setError(null);

            const params = { excelExport: true };
            if (searchTerm) params.searchTerm = searchTerm;
            if (companyFilter) params.companyId = companyFilter;
            if (accountTypeFilter) params.accountType = accountTypeFilter;

            const res = await axios.get(API.accounts_list, {
                params,
                responseType: "blob",
                headers: {},
            });

            // filename yakala (server Content-Disposition gönderiyorsa)
            const cd = res.headers?.["content-disposition"] || "";
            const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
            const filename = decodeURIComponent(match?.[1] || match?.[2] || "cariler.xlsx");

            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/login");
            } else {
                setError(err.response?.data?.message || "Excel dışarı aktarımında hata oluştu");
            }
        } finally {
            setExporting(false);
        }
    };

    const handleViewDetails = (id) => navigate(`/accounts/${id}`);
    const handleViewPayments = (id) => navigate(`/payment-history?accountId=${id}`);
    // Cari filtresinin dolabilmesi için firma da taşınır: kullanıcı listesindeki cari
    // seçimi, seçili firmanın carilerinden beslenir.
    const handleViewUsers = (a) => {
        const params = new URLSearchParams({ accountId: a.id });
        if (a.companyId) params.set("companyId", a.companyId);
        navigate(`/users?${params.toString()}`);
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(API.account(id));
            fetchAccounts(currentPage, pageSize, searchTerm, companyFilter, accountTypeFilter);
        } catch (err) {
            setError(err.response?.data?.message || "Cari/Bayi silinemedi");
        } finally {
            setDeleteModalOpen(false);
            setDeleteAccount(null);
        }
    };

    const handleAccountCreated = async () => {
        await fetchCompanies();
        await fetchAccounts(0, pageSize, searchTerm, companyFilter, accountTypeFilter);
        setCreateModalOpen(false);
    };

    useEffect(() => {
        (async () => {
            await fetchCompanies();
            fetchAccounts(0, pageSize, "", "", "", true);
            fetchCumulativeBalance();
        })();
    }, []);

    useEffect(() => {
        if (isFirstSearch.current) { isFirstSearch.current = false; return; }
        const t = setTimeout(() => fetchAccounts(0, pageSize, searchTerm, companyFilter, accountTypeFilter), 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const handlePageChange = (p) => {
        if (p >= 0 && p < totalPages) {
            setCurrentPage(p);
            fetchAccounts(p, pageSize, searchTerm, companyFilter, accountTypeFilter);
        }
    };

    const handlePageSizeChange = (sz) => {
        setPageSize(sz);
        setCurrentPage(0);
        fetchAccounts(0, sz, searchTerm, companyFilter, accountTypeFilter);
    };

    const handleCompanyFilterChange = (val) => {
        setCompanyFilter(val);
        setCurrentPage(0);
        fetchAccounts(0, pageSize, searchTerm, val, accountTypeFilter);
    };

    const handleAccountTypeFilterChange = (val) => {
        setAccountTypeFilter(val);
        setCurrentPage(0);
        fetchAccounts(0, pageSize, searchTerm, companyFilter, val);
    };

    const getPages = () => {
        const arr = [];
        const max = 5;
        let start = Math.max(0, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages - 1, start + max - 1);
        if (end - start + 1 < max) start = Math.max(0, end - max + 1);
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString("tr-TR", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

    const getAccountTypeBadgeColor = (t) => {
        switch (t) {
            case "SOLE_PROPRIETORSHIP": return "success";
            case "JOINT_STOCK_COMPANY": return "info";
            case "LIMITED_COMPANY": return "primary";
            case "COOPERATIVE": return "warning";
            case "GENERAL_PARTNERSHIP": return "secondary";
            case "COMMANDITE_COMPANY": return "dark";
            default: return "secondary";
        }
    };

    const translateAccountType = (t) => {
        switch (t) {
            case "SOLE_PROPRIETORSHIP": return "Şahıs Şirketi";
            case "JOINT_STOCK_COMPANY": return "Anonim Şirket";
            case "LIMITED_COMPANY": return "Limited Şirket";
            case "COOPERATIVE": return "Kooperatif";
            case "GENERAL_PARTNERSHIP": return "Kolektif Şirket";
            case "COMMANDITE_COMPANY": return "Komandit Şirket";
            default: return t;
        }
    };

    const getCompanyName = (id) => {
        const c = companies.find(x => x.id === id);
        return c ? c.companyName : id;
    };

    const formatCurrencyCode = (amount, code = "TRY") => {
        if (amount === null || amount === undefined) return "-";
        const num = parseFloat(amount);
        const formatted = new Intl.NumberFormat("tr-TR", { style: "currency", currency: code, minimumFractionDigits: 2 }).format(Math.abs(num));

        // Sıfır bakiye ne borç ne alacaktır; nötr gösterilir. Aksi hâlde "Alacak"
        // etiketiyle yeşil çıkıyor ve kapalı hesap alacaklıymış gibi okunuyordu.
        if (num === 0) return <span className="text-muted">{formatted}</span>;

        const isNegative = num < 0;
        const sign = isNegative ? "Borç" : "Alacak";
        return <span className={isNegative ? "text-danger" : "text-success"} title={`Toplam ${sign}`}>{formatted} ({sign})</span>;
    };

    /**
     * Kümülatif bakiye para birimi başına bir map'tir: { TRY, USD, EUR, GBP }.
     * Panelin diğer liste ekranlarındaki (Kullanıcılar, Kayıt Talepleri) gibi özet kartı
     * şeridine çevrilir: her para birimi bir kart, tutar sayı olarak büyük punto,
     * borç/alacak ayrımı hem alt satırda hem kartın üst şerit renginde.
     *
     * TRY her zaman gösterilir (bakiye sıfır olsa da ekranın boş kalmaması için);
     * diğer para birimleri yalnız sıfırdan farklıysa kart açar.
     */
    const balanceCards = useMemo(() => {
        const map = (cumulativeBalance && typeof cumulativeBalance === "object") ? cumulativeBalance : {};
        const entries = Object.entries(map).filter(([code, amt]) => code === "TRY" || parseFloat(amt) !== 0);
        const rows = entries.length ? entries : [["TRY", 0]];

        return rows.map(([code, amt]) => {
            const num = parseFloat(amt) || 0;
            const formatted = new Intl.NumberFormat("tr-TR", {
                style: "currency", currency: code, minimumFractionDigits: 2,
            }).format(Math.abs(num));

            // Sıfır bakiye ne borç ne alacaktır; nötr renk ve nötr açıklama alır.
            const hintText = num === 0 ? "Bakiye yok" : (num < 0 ? "Toplam borç" : "Toplam alacak");
            const accent = num === 0
                ? STAT_ACCENTS.navy
                : (num < 0 ? STAT_ACCENTS.red : STAT_ACCENTS.green);

            // Renk yalnız tutarda ve üst şeritte taşınır. Alt açıklama, StatCards'ın kendi
            // varsayılan tonunda (etiketle aynı gri) bırakılır: üç öğenin de renkli olması
            // kartı gereksiz vurguluyordu.
            return {
                key: code,
                label: `${code} Bakiye`,
                value: <span style={{ color: accent }}>{formatted}</span>,
                hint: hintText,
                accent,
            };
        });
    }, [cumulativeBalance]);

    return (
        <div className="page-content accounts-modern">
            <style>{styles}</style>
            <div className="container-fluid">
                {/* Kümülatif bakiye şeridi — tablonun üstünde, diğer liste ekranlarıyla aynı düzen. */}
                <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="text-muted" style={{ fontSize: ".82rem", fontWeight: 600 }}>
                        Kümülatif Toplam Bakiye
                    </span>
                </div>
                <StatCards className="mb-3" loading={balanceLoading} items={balanceCards} />

                <Row>
                    <Col>
                        <TableCard>
                            <CardHeader className="bg-white border-0 rounded-top-4" data-tour="accounts-header">
                                <div className="d-none d-md-block">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="mb-1 fw-bold text-dark">Cari/Bayi Yönetimi</h5>
                                            <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                                                {totalElements > 0 ? `Toplam ${totalElements} cari/bayi bulundu` : "Cari/Bayi bulunamadı"}
                                            </p>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <Button
                                                className="d-flex align-items-center gap-1 btn-rounded btn-orange"
                                                onClick={handleExportExcel}
                                                disabled={exporting || loading || refreshing}
                                                title="Filtrelenen carileri Excel olarak indir"
                                            >
                                                {exporting ? <Spinner size="sm" /> : <i className="bx bx-download" />}
                                                Dışarı Aktar
                                            </Button>
                                            <Button className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={() => navigate("/accounts/create")}>
                                                <i className="bx bx-plus" /> Yeni Cari/Bayi
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                        <select className="form-select pill-select" style={{ width: 220 }} value={companyFilter} onChange={(e) => handleCompanyFilterChange(e.target.value)}>
                                            <option value="">Tüm Firmalar</option>
                                            {companies.map((c) => (<option key={c.id} value={c.id}>{c.companyName}</option>))}
                                        </select>
                                        <select className="form-select pill-select" style={{ width: 200 }} value={accountTypeFilter} onChange={(e) => handleAccountTypeFilterChange(e.target.value)}>
                                            <option value="">Tüm Tipler</option>
                                            {ACCOUNT_TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                        <Input innerRef={searchRef} type="search" placeholder={animatedPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input pill-input" inputMode="search" autoComplete="off" autoCorrect="off" spellCheck={false} />
                                    </div>
                                </div>

                                <div className="d-md-none">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h5 className="mb-0 fw-semibold">Cariler/Bayiler</h5>
                                        <Button size="sm" className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={() => setCreateModalOpen(true)}>
                                            <i className="bx bx-plus" /> Ekle
                                        </Button>
                                    </div>
                                    <p className="mb-2 text-muted" style={{ fontSize: ".9rem" }}>
                                        {totalElements > 0 ? `Toplam ${totalElements} kayıt` : "Cari/Bayi bulunamadı"}
                                    </p>
                                    <div className="d-flex flex-column gap-2">
                                        <Input innerRef={searchRef} type="search" placeholder={animatedPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pill-input" inputMode="search" autoComplete="off" autoCorrect="off" spellCheck={false} />
                                        <select className="form-select pill-select" value={companyFilter} onChange={(e) => handleCompanyFilterChange(e.target.value)}>
                                            <option value="">Tüm Firmalar</option>
                                            {companies.map((c) => (<option key={c.id} value={c.id}>{c.companyName}</option>))}
                                        </select>
                                        <select className="form-select pill-select" value={accountTypeFilter} onChange={(e) => handleAccountTypeFilterChange(e.target.value)}>
                                            <option value="">Tüm Tipler</option>
                                            {ACCOUNT_TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardBody className="pt-2" data-tour="accounts-table">
                                {error && (
                                    <Alert color="danger" className="mb-3 rounded-4">
                                        <i className="bx bx-error-circle me-2" />
                                        {error}
                                        <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={() => fetchAccounts(currentPage, pageSize, searchTerm, companyFilter, accountTypeFilter)}>
                                            Tekrar Dene
                                        </button>
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner size="lg" color="primary" />
                                        <h6 className="mt-3 text-muted">Cariler yükleniyor...</h6>
                                    </div>
                                ) : (
                                    <>
                                        <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? 'opacity-50' : ''}`}>
                                            <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                {totalElements > 0 && (
                                                    <>Toplam <strong>{totalElements}</strong> kayıt • Sayfa <strong>{currentPage + 1}</strong> / {totalPages}</>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <button className="btn btn-light btn-sm btn-rounded" onClick={() => fetchAccounts(currentPage, pageSize, searchTerm, companyFilter, accountTypeFilter)} title="Yenile">
                                                    <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="table-responsive rounded-4 border">
                                            <Table className="table table-hover mb-0">
                                                <Thead>
                                                    <Tr>
                                                        <Th className="d-none d-sm-table-cell">Cari Kodu</Th>
                                                        <Th>Cari/Bayi Adı</Th>
                                                        <Th className="d-none d-sm-table-cell">Tip</Th>
                                                        <Th className="d-none d-lg-table-cell">Firma</Th>
                                                        <Th className="d-none d-md-table-cell">Telefon</Th>
                                                        <Th className="d-none d-xxl-table-cell">TC/VKN</Th>
                                                        <Th className="text-end">Net Bakiye</Th>
                                                        <Th>Durum</Th>
                                                        <Th className="text-end">İşlemler</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {accounts && accounts.length ? accounts.map((a, i) => (
                                                        <Tr key={a.id || i} className="border-bottom">
                                                            <Td className="d-none d-sm-table-cell"><span className="fw-semibold text-dark">{a.accountCode}</span></Td>
                                                            <Td>
                                                                <TruncatedText
                                                                    text={a.accountName}
                                                                    maxLength={18}
                                                                    className="fw-semibold text-dark"
                                                                />
                                                                {a.title && (
                                                                    <div className="text-muted small">
                                                                        <TruncatedText text={a.title} maxLength={24} />
                                                                    </div>
                                                                )}
                                                            </Td>
                                                            <Td className="d-none d-sm-table-cell">
                                                                <AccountTypeBadge type={a.accountType} />
                                                            </Td>
                                                            <Td className="d-none d-lg-table-cell text-dark">
                                                                <TruncatedText
                                                                    text={getCompanyName(a.companyId)}
                                                                    maxLength={18}
                                                                />
                                                            </Td>
                                                            <Td className="d-none d-md-table-cell">{a.phoneNumber || "-"}</Td>
                                                            <Td className="d-none d-xxl-table-cell">
                                                                {a.nationalId && <div className="small"><span className="text-muted">TC:</span> {a.nationalId}</div>}
                                                                {a.vknNumber && <div className="small"><span className="text-muted">VKN:</span> {a.vknNumber}</div>}
                                                                {!a.nationalId && !a.vknNumber && "-"}
                                                            </Td>
                                                            <Td className="text-end">{renderNetBalance(a)}</Td>
                                                            <Td>
                                <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: a.enabled ? "#71C30B" : "#E07A7A", color: "#fff" }}>
                                  {a.enabled ? "Aktif" : "Pasif"}
                                </span>
                                                            </Td>
                                                            <Td className="text-end">
                                                                <div className="d-flex gap-1 justify-content-end flex-wrap">
                                                                    <button className="btn btn-sm action-btn btn-navy" title="Düzenle" onClick={() => navigate(`/accounts/${a.id}/edit`)}>
                                                                        <i className="bx bx-edit" />
                                                                    </button>
                                                                    <button className="btn btn-sm action-btn btn-orange" title="Detay" onClick={() => handleViewDetails(a.id)}>
                                                                        <i className="bx bx-show" />
                                                                    </button>
                                                                    {/* Kullanıcı listesi accountId parametresiyle süzülür. */}
                                                                    <button
                                                                        className="btn btn-sm action-btn"
                                                                        style={{ background: "#221C5D", borderColor: "#221C5D", color: "#fff" }}
                                                                        title="Bu carinin kullanıcılarını gör"
                                                                        onClick={() => handleViewUsers(a)}
                                                                    >
                                                                        <FeatherIcon icon="users" size={14} style={{ verticalAlign: "middle" }} />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm action-btn"
                                                                        style={{ background: "#198754", borderColor: "#198754", color: "#fff" }}
                                                                        title="Ödeme geçmişini gör"
                                                                        onClick={() => handleViewPayments(a.id)}
                                                                    >
                                                                        <i className="bx bx-dollar-circle me-1" />
                                                                        Ödeme geçmişi
                                                                    </button>
                                                                    <button className="btn btn-sm action-btn" style={{ background: "#dc3545", borderColor: "#dc3545", color: "#fff" }} title="Sil" onClick={() => { setDeleteAccount(a); setDeleteModalOpen(true); }}>
                                                                        <i className="bx bx-trash" />
                                                                    </button>
                                                                </div>
                                                            </Td>
                                                        </Tr>
                                                    )) : (
                                                        <Tr>
                                                            <Td colSpan="10" className="text-center py-4">
                                                                <div className="text-muted">
                                                                    <i className="bx bx-building display-4 d-block mb-2" />
                                                                    Cari/Bayi bulunamadı
                                                                    {(searchTerm || companyFilter || accountTypeFilter) && (<div className="mt-2"><small>Filtreleri temizlemeyi deneyin</small></div>)}
                                                                    {!searchTerm && !companyFilter && !accountTypeFilter && totalElements === 0 && (
                                                                        <div className="mt-2"><Button className="btn-rounded btn-navy" size="sm" onClick={() => navigate("/accounts/create")}>İlk carinizi oluşturun</Button></div>
                                                                    )}
                                                                </div>
                                                            </Td>
                                                        </Tr>
                                                    )}
                                                </Tbody>
                                            </Table>
                                        </div>

                                        {totalElements > 0 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                    {totalElements} kayıttan {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} arası gösteriliyor
                                                </div>
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="d-flex align-items-center gap-2 page-size-wrap">
                                                        <span className="text-muted" style={{ fontSize: ".9rem" }}>Sayfa başına</span>
                                                        <select className="form-select form-select-sm rounded-pill page-size-select" value={pageSize} onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}>
                                                            {[10,25,50,100].map(s => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    </div>
                                                    <nav>
                                                        <ul className="pagination pagination-sm mb-0 corporate-pagination">
                                                            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                                                                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>
                                                                    <i className="bx bx-chevron-left" />
                                                                </button>
                                                            </li>
                                                            {getPages().map(p => (
                                                                <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                                    <button className="page-link" onClick={() => handlePageChange(p)}>{p + 1}</button>
                                                                </li>
                                                            ))}
                                                            <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
                                                                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
                                                                    <i className="bx bx-chevron-right" />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardBody>

                            <AccountDeleteModal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(!deleteModalOpen)} account={deleteAccount} onConfirm={handleDelete} />
                        </TableCard>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default AccountsTable;
